import Clarity from "@microsoft/clarity";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "store/hooks/useAuth";
import { getEnv } from "utils/pathEnv";

/**
 * Microsoft Clarity — analytics de comportamento (heatmaps e gravação de sessão).
 *
 * É OPT-IN por ambiente: só sobe quando `REACT_APP_CLARITY_PROJECT_ID` chega
 * preenchida no runtime (`docker run -e ...` → `docker/env.sh` → `window.env`).
 * Sem o id — o caso padrão em dev e em qualquer município que não contratou —
 * nada é injetado e nenhuma requisição sai para o clarity.ms.
 */

/**
 * Separa as duas SPAs dentro do mesmo projeto do Clarity. Sem isso, sessões do
 * colaborador e do cidadão ficam misturadas no mesmo dashboard.
 * Filtro no dashboard: Custom Filters → Custom tags → `aplicacao`.
 */
const TAG_APLICACAO = "cidax-cidadao";

let ativo = false;

/**
 * Injeta o script do Clarity. Chamada em `index.tsx` ANTES do `ReactDOM.render`:
 * o `identify` roda em efeito de componente e efeito de filho executa antes do
 * efeito do pai, então inicializar dentro de um `useEffect` do `App` deixaria a
 * primeira identificação para trás.
 *
 * O guard do id vazio é obrigatório: `Clarity.init("")` não valida nada e
 * injetaria `https://www.clarity.ms/tag/?ref=npm`, que só dá 404 no console.
 */
export function iniciarClarity(): void {
  const projectId = getEnv("REACT_APP_CLARITY_PROJECT_ID").trim();

  if (!projectId) return;

  // `init` é idempotente — a lib checa `#clarity-script` antes de injetar.
  Clarity.init(projectId);
  ativo = true;

  // Tag é de SESSÃO (ao contrário do identify, que a doc pede por página),
  // então uma chamada aqui basta. Vai para a fila do `window.clarity` e é
  // drenada quando o script remoto carrega.
  Clarity.setTag("aplicacao", TAG_APLICACAO);
}

/**
 * Associa a sessão gravada ao cidadão logado, para conseguir achar a gravação
 * de um chamado de suporte específico em vez de garimpar sessões anônimas.
 * Também costura celular e desktop do mesmo usuário, que sem isso contam como
 * duas pessoas.
 *
 * Boa parte do tráfego do cidadão é anônima (serviços públicos abertos, sem
 * login); nesse caso não há e-mail e a sessão fica anônima mesmo — mas segue
 * marcada pela tag `aplicacao`.
 *
 * O e-mail é hasheado no cliente pela própria lib antes de subir — o Clarity
 * guarda só o hash e refaz o hash do termo quando você filtra no dashboard.
 * O `nome` vai como `friendly-name` e **aparece em texto puro** no dashboard;
 * é justamente para isso que o parâmetro existe.
 *
 * O guard de `ativo` não é opcional: `Clarity.identify` chama `window.clarity`
 * direto, sem checar nada, e estoura `TypeError` se o script nunca foi injetado.
 */
function identificarUsuarioClarity(email?: string, nome?: string): void {
  if (!ativo || typeof (window as any).clarity !== "function") return;

  const identificador = email?.trim();
  if (!identificador) return;

  Clarity.identify(identificador, undefined, undefined, nome?.trim());
}

/**
 * Reidentifica o usuário no Clarity a cada troca de rota.
 *
 * A documentação da Identify API pede a chamada em **cada página** — numa SPA,
 * identificar só uma vez no boot atribui apenas a primeira tela da sessão.
 *
 * Precisa viver dentro do `<Router>` (por causa do `useLocation`) e dentro do
 * `<AuthProvider>` (por causa do `useAuth`); por isso é montado em `Routes`, e
 * não em `App`. Não renderiza nada.
 */
export const ClarityIdentify = () => {
  const user = useAuth();
  const { pathname } = useLocation();

  // Deps primitivas de propósito: `useAuth` devolve um objeto novo a cada
  // render, e usá-lo direto dispararia o efeito em toda renderização.
  useEffect(() => {
    identificarUsuarioClarity(user?.email, user?.nome);
  }, [user?.email, user?.nome, pathname]);

  return null;
};
